import { useMemo } from "react";

import { useTranslation } from "next-i18next";

import { Gender } from "@/Apollo/schema";
import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { isBagsProduct } from "@/common/utils/product.utils";
import { isSpecificAttribute } from "@/common/utils/type-guards.utils";
import {
  ATTRIBUTE_ADDITIONAL_INFO as ADDITIONAL_INFO,
  ADDITIONAL_ATTRIBUTE_ART_ORIENTATION as ART_ORIENTATION,
  ADDITIONAL_ATTRIBUTE_ART_SIZE as ART_SIZE,
  ATTRIBUTE_ARTIST,
  ATTRIBUTE_BRAND,
  ATTRIBUTE_COLOR,
  ATTRIBUTE_DESIGNER,
  ATTRIBUTE_FIRST_OWNER,
  ATTRIBUTE_LABEL,
  ATTRIBUTE_MATERIAL,
  ATTRIBUTE_STYLE,
  ATTRIBUTE_SUBJECT,
  ATTRIBUTE_USAGE,
  ADDITIONAL_ATTRIBUTE_DEPTH as DEPTH,
  ADDITIONAL_ATTRIBUTE_HEIGHT as HEIGHT,
  ADDITIONAL_ATTRIBUTE_NUMBER_OF_ITEMS as NUMBER_OF_ITEMS,
  PdpProductAttributes,
  ADDITIONAL_ATTRIBUTE_QUALITY as QUALITY,
  ADDITIONAL_ATTRIBUTE_SEAT_HEIGHT as SEAT_HEIGHT,
  ADDITIONAL_ATTRIBUTE_WIDTH as WIDTH,
} from "@/components/ProductDetail/static/pdp.static";

type AttributesField = keyof Omit<PdpProductAttributes, "hasAttributes">;
type ProductAttribute = ProductBySlugNonNullable["attributes"][0];

const createField = (
  obj: PdpProductAttributes,
  field: AttributesField
): PdpProductAttributes &
  Required<Pick<PdpProductAttributes, AttributesField>> => {
  if (!obj[field]) {
    obj[field] = [];
    obj.hasAttributes = true;
  }

  /** We are 100% sure this is the correct type at this point. */
  return obj as PdpProductAttributes &
    Required<Pick<PdpProductAttributes, AttributesField>>;
};

type ProductLabelAttribute = Extract<
  Required<ProductAttribute>,
  { __typename?: "Label" }
>;

const tryGetLabel = (
  result: PdpProductAttributes,
  attribute: ProductAttribute
) => {
  if (isSpecificAttribute<ProductLabelAttribute>(attribute, ATTRIBUTE_LABEL)) {
    const obj = createField(result, ATTRIBUTE_LABEL);
    obj[ATTRIBUTE_LABEL].push(attribute.slug);
  }
};

type ProductBrandAttribute = Extract<
  Required<ProductAttribute>,
  { __typename?: "Brand" }
>;

const tryGetBrand = (
  result: PdpProductAttributes,
  attribute: ProductAttribute
) => {
  if (isSpecificAttribute<ProductBrandAttribute>(attribute, ATTRIBUTE_BRAND)) {
    const obj = createField(result, ATTRIBUTE_BRAND);
    obj[ATTRIBUTE_BRAND].push(attribute.title);
  }
};

type ProductArtistAttribute = Extract<
  Required<ProductAttribute>,
  { __typename?: "Artist" }
>;

const tryGetArtist = (
  result: PdpProductAttributes,
  attribute: ProductAttribute
) => {
  if (
    isSpecificAttribute<ProductArtistAttribute>(attribute, ATTRIBUTE_ARTIST)
  ) {
    const obj = createField(result, ATTRIBUTE_ARTIST);
    obj[ATTRIBUTE_ARTIST].push(attribute.title);
  }
};

type ProductColorAttribute = Extract<
  Required<ProductAttribute>,
  { __typename?: "Color" }
>;

const tryGetColor = (
  result: PdpProductAttributes,
  attribute: ProductAttribute
) => {
  if (isSpecificAttribute<ProductColorAttribute>(attribute, ATTRIBUTE_COLOR)) {
    const obj = createField(result, ATTRIBUTE_COLOR);
    obj[ATTRIBUTE_COLOR].push({
      slug: attribute.slug,
      hex: attribute.hex ?? "",
    });
  }
};

type ProductDesignerAttribute = Extract<
  Required<ProductAttribute>,
  { __typename?: "Designer" }
>;

const tryGetDesigner = (
  result: PdpProductAttributes,
  attribute: ProductAttribute
) => {
  if (
    isSpecificAttribute<ProductDesignerAttribute>(attribute, ATTRIBUTE_DESIGNER)
  ) {
    const obj = createField(result, ATTRIBUTE_DESIGNER);
    obj[ATTRIBUTE_DESIGNER].push(attribute.title);
  }
};

type ProductMaterialAttribute = Extract<
  Required<ProductAttribute>,
  { __typename?: "Material" }
>;

const tryGetMaterial = (
  result: PdpProductAttributes,
  attribute: ProductAttribute
) => {
  if (
    isSpecificAttribute<ProductMaterialAttribute>(attribute, ATTRIBUTE_MATERIAL)
  ) {
    const obj = createField(result, ATTRIBUTE_MATERIAL);
    obj[ATTRIBUTE_MATERIAL].push(attribute.slug);
  }
};

type ProductStyleAttribute = Extract<
  Required<ProductAttribute>,
  { __typename?: "Style" }
>;

const tryGetStyle = (
  result: PdpProductAttributes,
  attribute: ProductAttribute
) => {
  if (isSpecificAttribute<ProductStyleAttribute>(attribute, ATTRIBUTE_STYLE)) {
    const obj = createField(result, ATTRIBUTE_STYLE);
    obj[ATTRIBUTE_STYLE].push(attribute.slug);
  }
};

type ProductAdditionalInfoAttribute = Extract<
  Required<ProductAttribute>,
  { __typename?: "AdditionalInfo" }
>;

const tryGetIsFirstOwner = (
  result: PdpProductAttributes,
  attribute: ProductAttribute
) => {
  if (
    isSpecificAttribute<ProductAdditionalInfoAttribute>(
      attribute,
      ADDITIONAL_INFO
    )
  ) {
    if (attribute.slug?.toLocaleLowerCase() === ATTRIBUTE_FIRST_OWNER) {
      const obj = createField(result, ATTRIBUTE_FIRST_OWNER);
      obj[ATTRIBUTE_FIRST_OWNER].push(true);
    }
  }
};

type ProductSubjectAttribute = Extract<
  Required<ProductAttribute>,
  { __typename?: "Subject" }
>;

const tryGetSubject = (
  result: PdpProductAttributes,
  attribute: ProductAttribute
) => {
  if (
    isSpecificAttribute<ProductSubjectAttribute>(attribute, ATTRIBUTE_SUBJECT)
  ) {
    const obj = createField(result, ATTRIBUTE_SUBJECT);
    obj[ATTRIBUTE_SUBJECT].push(attribute?.slug);
  }
};

type ProductUsageSignAttribute = Extract<
  Required<ProductAttribute>,
  { __typename?: "UsageSign" }
>;

const tryGetUsage = (
  result: PdpProductAttributes,
  attribute: ProductAttribute
) => {
  if (
    isSpecificAttribute<ProductUsageSignAttribute>(attribute, ATTRIBUTE_USAGE)
  ) {
    const obj = createField(result, ATTRIBUTE_USAGE);
    obj[ATTRIBUTE_USAGE].push(attribute.slug);
  }
};

export const useProductDetailsAttributes = (
  product: ProductBySlugNonNullable
) => {
  const { t } = useTranslation();

  const productAttributes = useMemo(() => {
    return product.attributes.reduce<PdpProductAttributes>((acc, curr) => {
      // Labels [slug]
      tryGetLabel(acc, curr);

      // Brands [slug]
      tryGetBrand(acc, curr);

      // Artist [title]
      tryGetArtist(acc, curr);

      // Color [{title, slug}]
      tryGetColor(acc, curr);

      // Designer [title]
      tryGetDesigner(acc, curr);

      // Material [slug]
      tryGetMaterial(acc, curr);

      // Style [slug]
      tryGetStyle(acc, curr);

      // First owner [boolean]
      tryGetIsFirstOwner(acc, curr);

      // Subject [slug]
      tryGetSubject(acc, curr);

      // Usage [slug]
      tryGetUsage(acc, curr);

      return acc;
    }, {});
  }, [product.attributes]);

  /** Add attributes that aren't in the attributes array. */
  const attributes = useMemo(() => {
    return Object.assign(productAttributes, {
      [QUALITY]: product?.[QUALITY] ? [product?.[QUALITY]] : [],
      [NUMBER_OF_ITEMS]: product?.[NUMBER_OF_ITEMS]
        ? [product?.[NUMBER_OF_ITEMS]]
        : [],
      [HEIGHT]: product?.[HEIGHT] ? [product?.[HEIGHT] + " cm"] : [],
      [WIDTH]: product?.[WIDTH] ? [product?.[WIDTH] + " cm"] : [],
      [DEPTH]: product?.[DEPTH] ? [product?.[DEPTH] + " cm"] : [],
      [SEAT_HEIGHT]: product?.[SEAT_HEIGHT]
        ? [product?.[SEAT_HEIGHT] + " cm"]
        : [],
      [ART_ORIENTATION]: product?.[ART_ORIENTATION]
        ? [product?.[ART_ORIENTATION]]
        : [],

      [ART_SIZE]: product?.[ART_SIZE] ? [product?.[ART_SIZE]] : [],
    });
  }, [productAttributes, product]);

  const quality = attributes[QUALITY]?.filter(Boolean).map(q =>
    t(`common:condition-${q.toLowerCase()}`)
  );

  const color = attributes[ATTRIBUTE_COLOR]?.filter(Boolean).map(c =>
    t(`color:color-${c.slug}`)
  );

  const material = attributes[ATTRIBUTE_MATERIAL]?.filter(Boolean).map(m =>
    t(`material:material-${m.toLowerCase()}`)
  );

  const numberOfItems = attributes[NUMBER_OF_ITEMS]?.filter(Boolean);

  const style = attributes[ATTRIBUTE_STYLE]?.filter(Boolean).map(s =>
    t(`style:style-${s.toLowerCase()}`)
  );

  const artist = attributes[ATTRIBUTE_ARTIST]?.filter(Boolean);

  const subject = attributes[ATTRIBUTE_SUBJECT]?.filter(Boolean).map(s =>
    t(`subject:subject-${s.toLowerCase()}`)
  );

  const designer = attributes[ATTRIBUTE_DESIGNER]?.filter(Boolean);
  const brand = attributes[ATTRIBUTE_BRAND]?.filter(Boolean);

  const firstOwner = attributes[ATTRIBUTE_FIRST_OWNER]?.filter(Boolean)[0]
    ? [t("common:common_yes_button")]
    : [];

  const artOrientation = attributes[ART_ORIENTATION]?.filter(Boolean).map(ao =>
    t(`pdp:pdp-attribute-orientation-${ao.toLowerCase()}`)
  );

  const artSize = attributes[ART_SIZE]?.filter(Boolean).map(as =>
    t(`pdp:pdp-attribute-art-size-${as.replace("_", "-").toLowerCase()}`)
  );

  const height = attributes[HEIGHT]?.filter(Boolean);
  const width = attributes[WIDTH]?.filter(Boolean);
  const depth = attributes[DEPTH]?.filter(Boolean);
  const seatHeight = attributes[SEAT_HEIGHT]?.filter(Boolean);

  const usage = attributes[ATTRIBUTE_USAGE]?.filter(Boolean).map(u =>
    t(`usagesign:usagesign-${u.toLowerCase()}`)
  );

  /** Bag-only attributes — see WHO-4690. */
  const isBags = isBagsProduct(product);

  const GENDER_LABEL_MAP: Record<Gender, string> = {
    [Gender.Men]: "common:common-gender-men",
    [Gender.Women]: "common:common-gender-women",
    [Gender.Unisex]: "common:common-gender-unisex",
  };
  const gender = product.gender ? [t(GENDER_LABEL_MAP[product.gender])] : [];
  const bagModel = product.bag_model ? [product.bag_model] : [];
  const hardwareColor = product.hardware_color?.title
    ? [product.hardware_color.title]
    : [];
  const yearOfPurchase = product.year_of_purchase
    ? [String(product.year_of_purchase)]
    : [];

  /** For bag products, hide Artist and Seat height (per WHO-4690). */
  const artistFiltered = isBags ? [] : artist;
  const seatHeightFiltered = isBags ? [] : seatHeight;

  return {
    quality,
    color,
    material,
    numberOfItems,
    style,
    artist: artistFiltered,
    subject,
    designer,
    brand,
    firstOwner,
    artOrientation,
    artSize,
    height,
    width,
    depth,
    seatHeight: seatHeightFiltered,
    usage,
    gender,
    bagModel,
    hardwareColor,
    yearOfPurchase,
  };
};
